// hooks/useCampaignTableFilters.js
import { useState, useMemo } from 'react';

export const useCampaignTableFilters = (data) => {
  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  
  // Filter state
  const [filterType, setFilterType] = useState('all');
  const [dateRange, setDateRange] = useState('all');
  const [selectedWebinars, setSelectedWebinars] = useState(new Set());
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  
  // Sorting state
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');

  // Filter and sort the data
  const filteredData = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    
    let filtered = [...data];
    
    // Apply search filter first
    if (searchTerm.trim()) {
      filtered = filtered.filter(row => 
        row.id.toString().includes(searchTerm) ||
        row.date.includes(searchTerm) ||
        (row.registrations?.total || 0).toString().includes(searchTerm)
      );
    }
    
    // Apply campaign-specific filters
    if (filterType === 'dateRange' && dateRange !== 'all') {
      const now = new Date();
      let cutoffDate;
      let endDate = now;
      
      if (dateRange === '7days') {
        cutoffDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      } else if (dateRange === '30days') {
        cutoffDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      } else if (dateRange === '90days') {
        cutoffDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
      } else if (dateRange === 'custom') {
        if (customStartDate) {
          cutoffDate = new Date(customStartDate);
        }
        if (customEndDate) {
          endDate = new Date(customEndDate);
          endDate.setHours(23, 59, 59, 999);
        }
      }
      
      if (cutoffDate) {
        filtered = filtered.filter(row => {
          const rowDate = new Date(row.date);
          return rowDate >= cutoffDate && rowDate <= endDate;
        });
      }
    } else if (filterType === 'webinars' && selectedWebinars.size > 0) {
      filtered = filtered.filter(row => selectedWebinars.has(row.id.toString()));
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch(sortBy) {
        case 'registrations':
          aValue = a.registrations?.total || 0;
          bValue = b.registrations?.total || 0;
          break;
        case 'attendees':
          aValue = a.attendees?.total || 0;
          bValue = b.attendees?.total || 0;
          break;
        case 'bookings':
          aValue = a.bookings?.total || 0;
          bValue = b.bookings?.total || 0;
          break;
        case 'sales':
          aValue = a.sales?.total || 0;
          bValue = b.sales?.total || 0;
          break;
        case 'date':
        default:
          aValue = new Date(a.date);
          bValue = new Date(b.date);
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
    
    return filtered;
  }, [data, searchTerm, sortBy, sortOrder, filterType, dateRange, selectedWebinars, customStartDate, customEndDate]);

  return {
    // Search
    searchTerm,
    setSearchTerm,
    
    // Filters
    filterType,
    setFilterType,
    dateRange,
    setDateRange,
    selectedWebinars,
    setSelectedWebinars,
    showFilterDropdown,
    setShowFilterDropdown,
    customStartDate,
    setCustomStartDate,
    customEndDate,
    setCustomEndDate,
    
    // Sorting
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    
    // Filtered data
    filteredData
  };
};